import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import Button from 'src/presentations/components/atom/button';
import Mail from 'src/assets/images/icons/mail.svg';
import { useGetFileUrl } from 'src/use-cases/invoice/useInvoices';
import { beautifyEnum } from 'src/utils/utility';
import { TransportMedium } from 'src/presentations/components/molecules/card/types';
import TransportMediumIcon from 'src/presentations/components/molecules/card/TransportMediumIcon';

import { useContactSelector } from 'src/presentations/components/molecules/contact/ContactSelector';
import DocumentGreen from 'src/assets/images/icons/document-text-green.svg';
import DocumentAccept from 'src/assets/images/icons/document-sketch.svg';
import CloseCircle from 'src/assets/images/icons/close-circle.svg';
import { useShipmentSelector } from '../ShipmentSelector';
import { useInvoiceContext } from '../InvoiceContextProvider';
import { NoContractForm, NoContractStep } from './types';

export const NoContractHeader = ({ activeStep }: { activeStep: NoContractStep }) => {
  const { invoiceSummary } = useInvoiceContext();
  const { shipment } = useShipmentSelector();
  const { setIsShowContact, setInvoice } = useContactSelector();
  const { watch } = useFormContext<NoContractForm>();
  const { verdict } = watch();

  const { refetch } = useGetFileUrl(invoiceSummary?.id);
  const onShowOriginalInvoice = async () => {
    const result = await refetch();
    if (result?.data) {
      window.open(result?.data, '_blank');
    }
  };

  const onClickContact = () => {
    const invoiceContact = {
      modality: invoiceSummary?.modality,
      provider: invoiceSummary?.serviceProvider,
      invoiceNumber: invoiceSummary?.invoiceNumber,
      id: invoiceSummary?.id,
      mblHbl: shipment?.mblHbl,
      status: invoiceSummary?.status,
      shipmentInvoiceId: shipment?.shipmentInvoiceId,
      shipmentNumber: shipment?.shipmentDetail?.shipmentNumber,
    };
    setIsShowContact(true);
    setInvoice(invoiceContact);
  };

  const title = useMemo(() => {
    if (activeStep === NoContractStep.Step1_Select) {
      return 'No Matching Rate Card Found';
    }
    if (verdict === 'setup-new') {
      return 'Setup a New Service';
    }
    if (verdict === 'accept') {
      return 'Accept as Spot Shipment';
    }
    if (verdict === 'reject') {
      return 'Reject Shipment';
    }
    return 'No Matching Rate Card Found';
  }, [verdict, activeStep]);

  const topIcon = useMemo(() => {
    if (activeStep === NoContractStep.Step1_Select) {
      return null;
    }
    if (verdict === 'setup-new') {
      return <img src={DocumentGreen} alt='' className='w-36' />;
    }
    if (verdict === 'accept') {
      return <img src={DocumentAccept} alt='' className='w-36' />;
    }
    if (verdict === 'reject') {
      return <img src={CloseCircle} alt='' className='w-36' />;
    }
    return 'No Matching Rate Card Found';
  }, [verdict, activeStep]);

  return (
    <>
      <div className='flex items-center gap-10 mt-4'>
        <div className='flex items-center justify-center'>
          <TransportMediumIcon medium={invoiceSummary?.modality as TransportMedium} iconColor='#9F9F9F' />
        </div>
        <h2 className='text-20 xl:text-24 font-bold text-blue_grey-80 max-lg:text-18'>{beautifyEnum(invoiceSummary?.serviceProvider)?.toUpperCase()}</h2>
        <div className='w-4 h-4 rounded-full bg-[#D9D9D9]' />
        <div className='uppercase text-neutral-60 text-16'>{invoiceSummary?.modality}</div>
      </div>
      <div className='flex place-items-center justify-between'>
        <div className='flex items-center gap-14 pt-20'>
          {topIcon}
          <div className='relative'>
            <h3 className='text-18 xl:text-24 font-bold'>{title}</h3>
            <div className='flex items-center gap-12 text-14 xl:text-16 leading-28 xl:leading-32 text-blue_grey-80'>
              <div>
                Invoice No. <strong className='text-primary'>{invoiceSummary?.invoiceNumber}</strong>
              </div>
              <div className='w-1 h-14 bg-neutral-20' />
              <div>
                <strong className='text-primary'>{shipment?.shipmentDetail?.shipmentNumber}</strong>
              </div>
            </div>
          </div>
        </div>
        <div className='flex gap-16'>
          <Button variant='neutral_outline' onClick={onClickContact}>
            <div className='flex gap-8'>
              Contact Freya <img src={Mail} alt='send mail' />
            </div>
          </Button>
          <Button variant='primary_outline' onClick={onShowOriginalInvoice}>
            View Original Invoice
          </Button>
        </div>
      </div>
    </>
  );
};
